import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { getCustomMeasureDashboard, getInsightDashboard, getRealtimeAnalytics } from "../../../../services/api.service";

interface InsightDashboardData {
    overview: {
        total_candidates: number;
        completed_interviews: number;
        average_completion_rate: number;
        average_duration: number;
        total_reports: number;
    };
    time_analytics: {
        daily_submissions: Record<string, number>;
        peak_hours: Record<string, number>;
    };
}

interface CustomMeasureDashboardData {
    total_active_candidates: number;
    candidates_with_report: number;
    status_counts: {
        READY: number;
        PROCESSING: number;
        ERROR: number;
        OTHER: number;
        NO_DOC: number;
    };
    score_distribution_0_10: Record<string, number>;
    hiring_recommendation: {
        hire: number;
        borderline: number;
        reject: number;
        unknown: number;
    };
}

interface RealtimeData {
    last_24_hours?: {
        average_completion_time?: number;
    };
}

interface Props {
    open: boolean;
    onClose: () => void;
    companyId: string | undefined;
    jobQuestionId: string | undefined;
}

const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds || !Number.isFinite(seconds)) return "0m";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const sec = Math.round(seconds % 60);
    if (mins < 60) return `${mins}m ${sec}s`;
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
};

const JobKpiDashboardModal: React.FC<Props> = ({ open, onClose, companyId, jobQuestionId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [insight, setInsight] = useState<InsightDashboardData | null>(null);
    const [customMeasure, setCustomMeasure] = useState<CustomMeasureDashboardData | null>(null);
    const [realtime, setRealtime] = useState<RealtimeData | null>(null);

    const load = useCallback(async () => {
        if (!companyId || !jobQuestionId) return;
        setLoading(true);
        setError(null);
        try {
            const [insightRes, cmRes, realtimeRes] = await Promise.all([
                getInsightDashboard(companyId, jobQuestionId),
                getCustomMeasureDashboard(companyId, jobQuestionId),
                getRealtimeAnalytics(companyId, jobQuestionId),
            ]);

            const insightBody = insightRes.data as { is_success?: boolean; data?: InsightDashboardData; error?: string };
            const cmBody = cmRes.data as { is_success?: boolean; data?: CustomMeasureDashboardData; error?: string };
            const realtimeBody = realtimeRes.data as { is_success?: boolean; data?: RealtimeData; error?: string };

            if (!insightBody?.is_success || !insightBody.data) {
                throw new Error(insightBody?.error || "โหลดข้อมูล insight ไม่สำเร็จ");
            }
            if (!cmBody?.is_success || !cmBody.data) {
                throw new Error(cmBody?.error || "โหลดข้อมูล custom measure ไม่สำเร็จ");
            }

            setInsight(insightBody.data);
            setCustomMeasure(cmBody.data);
            setRealtime(realtimeBody?.is_success && realtimeBody.data ? realtimeBody.data : null);
        } catch (e: any) {
            setError(e?.response?.data?.error || e?.message || "โหลด dashboard ไม่สำเร็จ");
        } finally {
            setLoading(false);
        }
    }, [companyId, jobQuestionId]);

    useEffect(() => {
        if (open && companyId && jobQuestionId) {
            void load();
        }
    }, [open, companyId, jobQuestionId, load]);

    const trends = useMemo(() => {
        const rows = Object.entries(insight?.time_analytics?.daily_submissions || {})
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => a.date.localeCompare(b.date));
        const dayTotal = rows.reduce((sum, row) => sum + row.count, 0);
        const weekTotal = rows.slice(-7).reduce((sum, row) => sum + row.count, 0);
        const monthTotal = rows.slice(-30).reduce((sum, row) => sum + row.count, 0);
        return {
            dayAvg: rows.length > 0 ? Math.round((dayTotal / rows.length) * 100) / 100 : 0,
            weekAvg: rows.length > 0 ? Math.round((weekTotal / Math.min(7, rows.length)) * 100) / 100 : 0,
            monthAvg: rows.length > 0 ? Math.round((monthTotal / Math.min(30, rows.length)) * 100) / 100 : 0,
            recentRows: rows.slice(-10),
        };
    }, [insight]);

    const peakUsage = useMemo(() => {
        const entries = Object.entries(insight?.time_analytics?.peak_hours || {});
        if (entries.length === 0) return { hour: "-", count: 0 };
        const [hour, count] = entries.reduce((max, cur) => (cur[1] > max[1] ? cur : max));
        return { hour, count };
    }, [insight]);

    const totalInterviewHours = useMemo(() => {
        if (!insight) return 0;
        return Math.round(((insight.overview.average_duration || 0) * (insight.overview.completed_interviews || 0) / 3600) * 10) / 10;
    }, [insight]);

    const quality = useMemo(() => {
        const hire = customMeasure?.hiring_recommendation?.hire || 0;
        const reject = customMeasure?.hiring_recommendation?.reject || 0;
        const borderline = customMeasure?.hiring_recommendation?.borderline || 0;
        const unknown = customMeasure?.hiring_recommendation?.unknown || 0;
        const total = hire + reject + borderline + unknown;
        const passRate = total > 0 ? Math.round((hire / total) * 100) : 0;
        const failRate = total > 0 ? Math.round((reject / total) * 100) : 0;
        return { passRate, failRate, reviewRate: Math.max(0, 100 - passRate - failRate) };
    }, [customMeasure]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
            <DialogTitle>
                <Typography variant="h6" fontWeight={700}>Dashboard — Job KPI (Report + Custom Measure)</Typography>
                <Typography variant="body2" color="text.secondary">
                    ระดับงานเดียวตาม job question นี้
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
                        <CircularProgress size={28} />
                        <Typography>กำลังโหลดข้อมูล KPI...</Typography>
                    </Box>
                )}
                {!loading && error && <Alert severity="error">{error}</Alert>}
                {!loading && !error && insight && customMeasure && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
                            <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Total Interviews Conducted</Typography><Typography variant="h5" fontWeight={700}>{(insight.overview.total_reports || 0).toLocaleString()}</Typography></CardContent></Card>
                            <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Total Clients Using System</Typography><Typography variant="h5" fontWeight={700}>{insight.overview.total_reports > 0 ? 1 : 0}</Typography></CardContent></Card>
                            <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Total Candidates Processed</Typography><Typography variant="h5" fontWeight={700}>{Math.max(insight.overview.total_candidates || 0, customMeasure.total_active_candidates || 0).toLocaleString()}</Typography></CardContent></Card>
                            <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Total Interview Hours Conducted</Typography><Typography variant="h5" fontWeight={700}>{totalInterviewHours.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">estimated from avg duration x completed</Typography></CardContent></Card>
                        </Box>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700}>Usage Volume & Adoption</Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                    <Chip label={`Interviews/day avg: ${trends.dayAvg}`} />
                                    <Chip label={`Interviews/week avg: ${trends.weekAvg}`} />
                                    <Chip label={`Interviews/month avg: ${trends.monthAvg}`} />
                                    <Chip label={`Peak usage: ${peakUsage.hour}:00 (${peakUsage.count})`} color="primary" variant="outlined" />
                                    <Chip label={`Active clients: ${insight.overview.total_reports > 0 ? 1 : 0}`} color="success" variant="outlined" />
                                    <Chip label={`Inactive clients: ${insight.overview.total_reports > 0 ? 0 : 1}`} color="default" variant="outlined" />
                                </Box>
                                <Table size="small" sx={{ mt: 1.5 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Recent Date</TableCell>
                                            <TableCell align="right">Interviews</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {trends.recentRows.length === 0 && (
                                            <TableRow><TableCell colSpan={2}><Typography variant="caption" color="text.secondary">ไม่มีข้อมูลรายวัน</Typography></TableCell></TableRow>
                                        )}
                                        {trends.recentRows.map((row) => (
                                            <TableRow key={row.date}>
                                                <TableCell>{row.date}</TableCell>
                                                <TableCell align="right">{row.count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="body2" fontWeight={600}>Interviews per client</Typography>
                                    <Typography variant="caption" color="text.secondary">ในระดับ job นี้มี 1 client (`company_id`) จึงแสดงเป็น single-row leaderboard</Typography>
                                    <Table size="small" sx={{ mt: 0.5 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Client</TableCell>
                                                <TableCell align="right">Interviews</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>{companyId || "-"}</TableCell>
                                                <TableCell align="right">{insight.overview.total_reports || 0}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700}>Efficiency & Time Savings</Typography>
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5, mt: 1 }}>
                                    <Box><Typography variant="caption" color="text.secondary">Average interview duration</Typography><Typography variant="h6">{formatDuration(insight.overview.average_duration)}</Typography></Box>
                                    <Box><Typography variant="caption" color="text.secondary">Time to complete interview (candidate side)</Typography><Typography variant="h6">{formatDuration(realtime?.last_24_hours?.average_completion_time ?? insight.overview.average_duration)}</Typography></Box>
                                    <Box><Typography variant="caption" color="text.secondary">Screening turnaround time</Typography><Typography variant="h6">{formatDuration(realtime?.last_24_hours?.average_completion_time ?? insight.overview.average_duration)}</Typography></Box>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700}>Interview Quality & Outcomes</Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                    <Chip label={`Completion rate: ${insight.overview.average_completion_rate || 0}%`} color="success" />
                                    <Chip label={`Drop-off rate: ${Math.max(0, 100 - (insight.overview.average_completion_rate || 0))}%`} color="warning" />
                                    <Chip label={`Pass rate: ${quality.passRate}%`} color="success" variant="outlined" />
                                    <Chip label={`Fail rate: ${quality.failRate}%`} color="error" variant="outlined" />
                                    <Chip label={`Review needed: ${quality.reviewRate}%`} variant="outlined" />
                                </Box>

                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="body2" fontWeight={600}>Drop-off breakdown (proxy from custom-measure status)</Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.75 }}>
                                    <Chip label={`READY ${customMeasure.status_counts.READY || 0}`} color="success" variant="outlined" />
                                    <Chip label={`PROCESSING ${customMeasure.status_counts.PROCESSING || 0}`} color="warning" variant="outlined" />
                                    <Chip label={`ERROR ${customMeasure.status_counts.ERROR || 0}`} color="error" variant="outlined" />
                                    <Chip label={`NO_DOC ${customMeasure.status_counts.NO_DOC || 0}`} variant="outlined" />
                                </Box>

                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="body2" fontWeight={600}>Average candidate score distribution (Custom Measure 0-10)</Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.75 }}>
                                    {["0-2", "2-4", "4-6", "6-8", "8-10"].map((bucket) => (
                                        <Chip key={bucket} label={`${bucket}: ${customMeasure.score_distribution_0_10?.[bucket] || 0}`} />
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={() => void load()} disabled={loading}>รีเฟรช</Button>
                <Button variant="contained" onClick={onClose}>ปิด</Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobKpiDashboardModal;
